import { useQuery } from "@tanstack/react-query";
import { auxiliaryQueryKeys } from "../queryKeys";

interface StatsFetchState<T> {
  data: T | null;
  isLoading: boolean;
  error: string | null;
}

function createStatsQueryKey(endpoint: string) {
  try {
    const url = new URL(endpoint, window.location.origin);
    const filter = url.searchParams.toString();
    return auxiliaryQueryKeys.stats({
      baseUrl: url.origin,
      resource: url.pathname,
      ...(filter === "" ? {} : { filter }),
    });
  } catch {
    return auxiliaryQueryKeys.stats({
      baseUrl: "unresolved-endpoint",
      resource: endpoint,
    });
  }
}

async function fetchStats<T>(endpoint: string): Promise<T> {
  const response = await fetch(endpoint, {
    method: "GET",
    headers: { Accept: "application/json" },
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  return (await response.json()) as T;
}

export function useStatsFetch<T>(
  endpoint: string,
  errorLabel = "統計",
): StatsFetchState<T> {
  const query = useQuery<T, Error>({
    queryKey: createStatsQueryKey(endpoint),
    queryFn: () => fetchStats<T>(endpoint),
    retry: false,
  });

  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error === null ? null : `${errorLabel}の取得に失敗しました (${query.error.message})`,
  };
}
